import React, { useMemo, useRef } from 'react'
import type { CSSProperties } from 'react'
import type { AreaChartPoint } from '../AreaChart/AreaChart.types'
import type { LineChartProps, LineChartSeries } from './LineChart.types'
import {
  LINE_CHART_DEFAULT_DATA,
  LINE_CHART_DEFAULT_HEIGHT,
  LINE_CHART_DEFAULT_STROKE_WIDTH,
  LINE_CHART_DEFAULT_WIDTH,
  LINE_CHART_SERIES_COLORS,
  LINE_CHART_STROKE_COLOR,
} from './LineChart.types'
import { ChartTooltip, useSeriesChartTooltip } from '../ChartTooltip'
import './LineChart.css'

type PlotPoint = { x: number; y: number }

const resolvePoints = (data: AreaChartPoint[]): AreaChartPoint[] => {
  const usable = data.filter((point) => Number.isFinite(point.value))
  return usable.length > 0 ? usable : LINE_CHART_DEFAULT_DATA
}

const buildPlotPoints = (
  data: AreaChartPoint[],
  width: number,
  height: number,
  minValue: number,
  maxValue: number,
  insetY: number,
): PlotPoint[] => {
  const range = Math.max(maxValue - minValue, Number.EPSILON)
  const lastIndex = Math.max(data.length - 1, 1)
  const plotHeight = Math.max(height - insetY * 2, 0)

  return data.map((point, index) => {
    const x = data.length === 1 ? 0 : (index / lastIndex) * width
    const normalized = (point.value - minValue) / range
    const y = insetY + (1 - normalized) * plotHeight
    return { x, y }
  })
}

const buildLinePath = (points: PlotPoint[]): string => {
  if (points.length === 0) return ''
  return points
    .map((point, index) => `${index === 0 ? 'M' : 'L'} ${point.x} ${point.y}`)
    .join(' ')
}

const resolveSeriesList = (
  series: LineChartSeries[] | undefined,
  data: AreaChartPoint[],
  seriesName: string,
  strokeColor: string,
): LineChartSeries[] => {
  if (series && series.length > 0) return series
  return [
    {
      name: seriesName,
      data,
      color: strokeColor,
    },
  ]
}

const LineChart: React.FC<LineChartProps> = ({
  data = LINE_CHART_DEFAULT_DATA,
  series,
  width = LINE_CHART_DEFAULT_WIDTH,
  height = LINE_CHART_DEFAULT_HEIGHT,
  strokeColor = LINE_CHART_STROKE_COLOR,
  strokeWidth = LINE_CHART_DEFAULT_STROKE_WIDTH,
  maxValue,
  minValue = 0,
  showTooltip = true,
  seriesName = 'Value',
  formatValue,
  darkMode = false,
  className = '',
  style,
  'aria-label': ariaLabel,
}) => {
  const containerRef = useRef<HTMLDivElement>(null)
  const format = formatValue ?? ((value: number) => String(value))

  const resolvedSeries = useMemo(
    () => resolveSeriesList(series, data, seriesName, strokeColor),
    [data, series, seriesName, strokeColor],
  )

  const prepared = useMemo(
    () =>
      resolvedSeries.map((item, index) => {
        const points = resolvePoints(item.data)
        const color =
          item.color ??
          LINE_CHART_SERIES_COLORS[index % LINE_CHART_SERIES_COLORS.length]
        return {
          name: item.name,
          color,
          strokeWidth: item.strokeWidth ?? strokeWidth,
          points,
        }
      }),
    [resolvedSeries, strokeWidth],
  )

  const insetY = Math.max(strokeWidth / 2 + 1, 2)

  const resolvedMax = useMemo(() => {
    if (maxValue != null && Number.isFinite(maxValue)) return maxValue
    const peak = Math.max(
      ...prepared.flatMap((item) => item.points.map((point) => point.value)),
      minValue + 1,
    )
    return peak > minValue ? peak : minValue + 1
  }, [maxValue, minValue, prepared])

  const seriesPaths = useMemo(
    () =>
      prepared.map((item) => {
        const plotPoints = buildPlotPoints(
          item.points,
          width,
          height,
          minValue,
          resolvedMax,
          insetY,
        )
        return {
          ...item,
          plotPoints,
          path: buildLinePath(plotPoints),
        }
      }),
    [height, insetY, minValue, prepared, resolvedMax, width],
  )

  /** Use the longest series for category / nearest-x hover. */
  const hoverSeries = useMemo(() => {
    if (seriesPaths.length === 0) return null
    return seriesPaths.reduce((longest, item) =>
      item.plotPoints.length > longest.plotPoints.length ? item : longest,
    )
  }, [seriesPaths])

  const { tooltip, onMouseMove, onMouseLeave } = useSeriesChartTooltip({
    enabled: showTooltip && Boolean(hoverSeries),
    containerRef,
    plotPoints: hoverSeries?.plotPoints ?? [],
    viewWidth: width,
    viewHeight: height,
  })

  const activeIndex = tooltip.visible ? tooltip.index : -1
  const categoryLabel =
    activeIndex >= 0
      ? (hoverSeries?.points[activeIndex]?.label ?? `Point ${activeIndex + 1}`)
      : undefined

  const tooltipItems =
    activeIndex >= 0
      ? seriesPaths.map((item) => {
          const point = item.points[activeIndex]
          return {
            label: item.name,
            value: point ? format(point.value) : '—',
            color: item.color,
          }
        })
      : []

  const classNames = [
    'ds-line-chart',
    showTooltip ? 'ds-line-chart--interactive' : '',
    className,
  ]
    .filter(Boolean)
    .join(' ')

  const rootStyle = {
    ...style,
    '--ds-line-chart-height': `${height}px`,
  } as CSSProperties

  return (
    <div
      ref={containerRef}
      className={classNames}
      style={rootStyle}
      role="img"
      aria-label={ariaLabel ?? 'Line chart'}
      onMouseMove={showTooltip ? onMouseMove : undefined}
      onMouseLeave={showTooltip ? onMouseLeave : undefined}
    >
      <svg
        className="ds-line-chart__svg"
        viewBox={`0 0 ${width} ${height}`}
        width="100%"
        height="100%"
        preserveAspectRatio="none"
        aria-hidden="true"
        overflow="visible"
      >
        {seriesPaths.map((item) => (
          <path
            key={item.name}
            d={item.path}
            fill="none"
            stroke={item.color}
            strokeWidth={item.strokeWidth}
            strokeLinecap="round"
            strokeLinejoin="miter"
            vectorEffect="non-scaling-stroke"
          />
        ))}
      </svg>

      {showTooltip && tooltip.visible && hoverSeries ? (
        <div
          className={`ds-chart-tooltip-layer${
            darkMode ? ' ds-chart-tooltip-layer--dark' : ''
          }`}
          aria-hidden="true"
        >
          <span
            className="ds-chart-tooltip-crosshair"
            style={{ left: tooltip.pointX }}
          />
          {seriesPaths.map((item) => {
            const point = item.plotPoints[activeIndex]
            if (!point) return null
            return (
              <span
                key={`marker-${item.name}`}
                className="ds-chart-tooltip-marker"
                style={{
                  left: `${(point.x / width) * 100}%`,
                  top: `${(point.y / height) * 100}%`,
                  background: item.color,
                }}
              />
            )
          })}
        </div>
      ) : null}

      {showTooltip && activeIndex >= 0 ? (
        <ChartTooltip
          visible
          darkMode={darkMode}
          title={categoryLabel}
          x={tooltip.pointX}
          y={tooltip.pointY}
          items={tooltipItems}
        />
      ) : null}
    </div>
  )
}

export default LineChart
